import React, { createContext, useContext, useState } from 'react';
import './AlertProvider.css';

const AlertContext = createContext(undefined);

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

export const AlertProvider = ({ children }) => {
    const [visible, setVisible] = useState(false);
    const [config, setConfig] = useState({
        type: 'info',
        title: '',
        message: '',
        buttons: [],
    });

    const showAlert = (alertConfig) => {
        setConfig({
            type: alertConfig.type || 'info',
            title: alertConfig.title,
            message: alertConfig.message,
            buttons: alertConfig.buttons || [{ text: 'OK', style: 'default' }],
        });
        setVisible(true);
    };

    const showSuccess = (title, message, onOk) => {
        showAlert({
            type: 'success',
            title,
            message,
            buttons: [{ text: 'OK', onPress: onOk, style: 'default' }],
        });
    };

    const showError = (title, message, onOk) => {
        showAlert({
            type: 'error',
            title: 'Failed',
            message,
            buttons: [{ text: 'OK', onPress: onOk, style: 'default' }],
        });
    };

    const showWarning = (title, message, onOk) => {
        showAlert({
            type: 'warning',
            title,
            message,
            buttons: [{ text: 'OK', onPress: onOk, style: 'default' }],
        });
    };

    const showInfo = (title, message, onOk) => {
        showAlert({
            type: 'info',
            title,
            message,
            buttons: [{ text: 'OK', onPress: onOk, style: 'default' }],
        });
    };

    const showConfirm = (title, message, onConfirm, onCancel) => {
        showAlert({
            type: 'confirm',
            title,
            message,
            buttons: [
                { text: 'Cancel', onPress: onCancel, style: 'cancel' },
                { text: 'Confirm', onPress: onConfirm, style: 'default' },
            ],
        });
    };

    const hideAlert = () => {
        setVisible(false);
    };

    const handleButtonPress = (button) => {
        hideAlert();
        if (button.onPress) {
            setTimeout(() => {
                button.onPress?.();
            }, 150);
        }
    };

    const getAlertIcon = (type) => {
        switch (type) {
            case 'success':
                return { icon: 'check_circle', color: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.1)' };
            case 'error':
                return { icon: 'error', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' };
            case 'warning':
                return { icon: 'warning', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)' };
            case 'info':
                return { icon: 'info', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)' };
            case 'confirm':
                return { icon: 'help', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)' };
            default:
                return { icon: 'info', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)' };
        }
    };

    const iconConfig = getAlertIcon(config.type);

    return (
        <AlertContext.Provider value={{ showAlert, showSuccess, showError, showWarning, showInfo, showConfirm, hideAlert }}>
            {children}
            {visible && (
                <div className="alert-overlay">
                    <div className="alert-container">
                        <div className="alert-icon" style={{ backgroundColor: iconConfig.bgColor }}>
                            <span className="material-icons" style={{ color: iconConfig.color, fontSize: 40 }}>
                                {iconConfig.icon}
                            </span>
                        </div>
                        <h2 className="alert-title">{config.title}</h2>
                        {config.message && <p className="alert-message">{config.message}</p>}
                        <div className="alert-buttons">
                            {config.buttons?.map((button, index) => (
                                <button
                                    key={index}
                                    className={`alert-button ${button.style === 'cancel' ? 'cancel' : button.style === 'destructive' ? 'destructive' : 'default'}`}
                                    onClick={() => handleButtonPress(button)}
                                >
                                    {button.text}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </AlertContext.Provider>
    );
};

export default AlertProvider;
